import { Alert, Box, Paper, Typography } from "@mui/material";
import { ReactNode } from "react";

import api from "services/api";

import { TituloComSelo } from "./Visibilidade";
import { GenteResposta, IRhAbas, NomeAba, PrevistoResposta, SeloVisibilidadeAba, VisibilidadeResposta } from "./types";

export async function getRhAbas(): Promise<IRhAbas> {
  const { data } = await api.get<IRhAbas>("/insights/rh/abas");
  return data;
}

/** Mesma regra da aba Hoje: 401 e 403 escondem; erro de rede mostra a aba com a mensagem. */
export function mostrarAbas(q: { data?: IRhAbas; error: unknown }): boolean {
  if (q.error) {
    const status = (q.error as { response?: { status?: number } }).response?.status;
    return status !== 401 && status !== 403;
  }
  return q.data?.estado === "OK";
}

export async function getRhVisibilidade(): Promise<VisibilidadeResposta> {
  const { data } = await api.get<VisibilidadeResposta>("/insights/rh/visibilidade");
  return data;
}

export async function getRhGente(): Promise<GenteResposta> {
  const { data } = await api.get<GenteResposta>("/insights/rh/gente");
  return data;
}

export async function getRhPrevisto(): Promise<PrevistoResposta> {
  const { data } = await api.get<PrevistoResposta>("/insights/rh/previsto");
  return data;
}

/** true só com a linha da aba presente e visível. */
export const abaVisivel = (v: VisibilidadeResposta | undefined, aba: NomeAba): boolean =>
  v?.abas.find((a) => a.aba === aba)?.visivel === true;

/** Selo da linha: vem de `selo`, não da classe; null ou ausente = sem selo. */
export const seloDaAba = (v: VisibilidadeResposta | undefined, aba: NomeAba): SeloVisibilidadeAba | null | undefined =>
  v?.abas.find((a) => a.aba === aba)?.selo;

/** Erro na visibilidade não abre a aba (é ela que diz quem vê); 401/403 da resposta escondem; carregando esconde. */
export function mostrarAbaDaFolha(
  aba: "previsto" | "gente",
  vis: { data?: VisibilidadeResposta; error: unknown },
  q: { data?: { disponivel: boolean }; error: unknown },
): boolean {
  if (vis.error || !abaVisivel(vis.data, aba)) return false;
  if (q.error) {
    const status = (q.error as { response?: { status?: number } }).response?.status;
    return status !== 401 && status !== 403;
  }
  return q.data !== undefined;
}

export const num = (n: number) => n.toLocaleString("pt-BR");
/** Nome do benefício para o RH; o cadastro diz "Saúde" porque o nome entra no rótulo do contrato. */
const NOME_PRODUTO: Record<string, string> = { Saúde: "Plano de saúde", Odonto: "Plano odontológico", Vida: "Seguro de vida" };
export const nomeProduto = (produto: string) => NOME_PRODUTO[produto] ?? produto;
/** Fração (0 a 1) em porcentagem inteira. */
export const pctInt = (x: number) => `${Math.round(x * 100)}%`;
export const pct = (n: number, total: number) =>
  total <= 0 ? "" : `${((n / total) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
/** Barra da estimativa e parte que muda de faixa. */
export const listrado = (cor: string) => `repeating-linear-gradient(45deg, ${cor} 0 3px, #FFFFFF 3px 7px)`;
export const listaPt = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} e ${xs[xs.length - 1]}`);
export const reais = (v: number) =>
  v >= 1_000_000
    ? `R$ ${(v / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} milhões`
    : v >= 10_000
      ? `R$ ${Math.round(v / 1000).toLocaleString("pt-BR")} mil`
      : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
export const plural = (n: number, um: string, varios: string) => `${num(n)} ${n === 1 ? um : varios}`;
/** `lidoEm` e data sem hora (AAAA-MM-DD): formata por texto, nunca por Date (fuso). */
export const dataSemHora = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("/") : iso);
export const dataBr =(iso: string) => iso.split("-").reverse().join("/");
/** Dia civil de Brasília de um instante ISO (o servidor guarda UTC). */
export const diaBrt = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });

/** Só `SEM_CONTRATO` tem frase própria; qualquer outro motivo, inclusive um que a tela não conhece, é "indisponível". */
export const textoSemCadastro = (motivo: string): string =>
  motivo === "SEM_CONTRATO" ? "Depende de um contrato desta empresa na Conducco." : "Cadastro desta empresa indisponível.";

export function SemCadastro({ motivo, testid }: { motivo: string; testid: string }) {
  return (
    <Typography variant="body2" color="text.secondary" data-testid={testid}>
      {textoSemCadastro(motivo)}
    </Typography>
  );
}

export function Cartao({ titulo, subtitulo, children }: { titulo: string; subtitulo?: string; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography component="h3" variant="h6" sx={{ fontFamily: "Sora", fontWeight: 700 }}>
          {titulo}
        </Typography>
        {subtitulo && (
          <Typography variant="body2" color="text.secondary">
            {subtitulo}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

/** `valor` nulo = não apurado: texto discreto, nunca com cara de número. */
export function Kpi({ rotulo, valor, nota, cor }: { rotulo: string; valor: string | null; nota?: string; cor?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>
      {valor === null ? (
        <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.6 }}>não apurado</Typography>
      ) : (
        <Typography sx={{ fontFamily: "Sora", fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, color: cor }}>
          {valor}
        </Typography>
      )}
      {nota && (
        <Typography variant="body2" color="text.secondary">
          {nota}
        </Typography>
      )}
    </Paper>
  );
}

/** Barra horizontal: rótulo, barra proporcional ao maior, valor. */
export function Barras({ itens, cor = "#3024E1", cinza }: { itens: { rotulo: string; n: number; nota?: string }[]; cor?: string; cinza?: string }) {
  const max = Math.max(1, ...itens.map((i) => i.n));
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {itens.map((i) => (
        <Box key={i.rotulo} display="grid" gridTemplateColumns="minmax(110px, 200px) minmax(0, 1fr) auto" gap={1.5} alignItems="center">
          <Typography variant="body2" noWrap title={i.rotulo}>
            {i.rotulo}
          </Typography>
          <Box sx={{ height: 14, bgcolor: "grey.100", borderRadius: 1 }}>
            <Box
              sx={{
                height: 14,
                borderRadius: 1,
                width: `${Math.max(i.n ? 1 : 0, (i.n / max) * 100)}%`,
                bgcolor: i.rotulo === cinza ? "grey.400" : cor,
              }}
            />
          </Box>
          <Typography variant="body2" fontWeight={600} textAlign="right" data-testid={`barra-nota-${i.rotulo}`}>
            {i.nota ?? num(i.n)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function Cabecalho({
  sobretitulo,
  titulo,
  dados,
  unidade,
  aoLadoDoTitulo,
}: {
  sobretitulo: string;
  titulo: string;
  dados: Pick<IRhAbas, "diaDosNumeros" | "avisos">;
  /** Como esta aba conta: por pessoa (Ativos) ou por inscrição (Compras). */
  unidade?: string;
  aoLadoDoTitulo?: ReactNode;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="overline" color="primary.main" fontWeight={600}>
        {sobretitulo}
      </Typography>
      <TituloComSelo titulo={titulo} aoLado={aoLadoDoTitulo} />
      {dados.diaDosNumeros && (
        <Typography variant="body2" color="text.secondary">
          Números da foto de {dataBr(dados.diaDosNumeros)}
          {unidade ? ` · ${unidade}` : ""}
        </Typography>
      )}
      {dados.avisos.map((a) => (
        <Alert key={a} severity="info" sx={{ mt: 1 }}>
          {a}
        </Alert>
      ))}
    </Box>
  );
}

export function ErroAba({ nome }: { nome: string }) {
  return <Alert severity="error">Não foi possível carregar a aba {nome}. Tente de novo em alguns minutos.</Alert>;
}
